import {
  AuthTransaction,
  TransactionFactor,
  FactorStatus,
} from "../types/authTransaction";
import {
  Factor,
  FactorKey,
  FactorTypeEnum,
  Provider,
} from "types/factor/factor";
import {
  ParsedFactorMap,
  FACTOR_ORDER,
  ParsedFactorData,
} from "../types/parsedTransactionFactorData";

const getFactorKey = ({
  factorType,
  provider,
}: TransactionFactor): FactorKey => {
  if (factorType === FactorTypeEnum.Sms) {
    return FactorKey.Sms;
  } else if (factorType === FactorTypeEnum.Call) {
    return FactorKey.Call;
  } else if (factorType === FactorTypeEnum.WebAuthn) {
    return FactorKey.WebAuthn;
  }

  // Google Auth and Okta Verify have the same factorType
  // Need to use the provider attribute to make a distinction between the 2
  if (provider === Provider.Google) {
    return FactorKey.GoogleAuth;
  } else {
    return FactorKey.OktaVerify;
  }
};

const parseFactor = (transactionFactor: TransactionFactor): Factor => {
  return {
    id: "", // transaction factors have no id until they are enrolled
    factorType: transactionFactor.factorType,
    provider: transactionFactor.provider,
    key: getFactorKey(transactionFactor),
    flags: {
      isActive: transactionFactor.status === FactorStatus.Active,
      isPhoneFactor:
        transactionFactor.factorType === FactorTypeEnum.Sms ||
        transactionFactor.factorType === FactorTypeEnum.Call,
      isTotpFactor: transactionFactor.factorType === FactorTypeEnum.Totp,
      isPasskeyFactor: transactionFactor.factorType === FactorTypeEnum.WebAuthn,
    },
    // The phone number in the profile will exist after successfully enrolling in a phone factor.
    profile: transactionFactor.phones?.[0].profile ?? {},
  };
};

// Intended specifically for parsing through the factor list from the transaction response
// This data will be stripped down to whatever is essential because the object will be stored in localStorage
const parseTransactionFactorList = (
  transaction: AuthTransaction,
): ParsedFactorData => {
  if (!transaction.factors)
    return {
      factorMap: {},
      factorOrder: [],
    };

  const factorMap = transaction.factors
    .map(parseFactor)
    .reduce((map, factor) => {
      map[factor.key] = factor;
      return map;
    }, {} as ParsedFactorMap);

  return {
    factorMap,
    factorOrder: FACTOR_ORDER.filter((key) => factorMap[key]),
  };
};

export { parseTransactionFactorList };
